/**
 * GlobalAmbientAudio — app-wide ambient soundscape.
 *
 * Single persistent audio engine mounted at the root of the router.
 * Plays a real MP3 (/media/background.mp3 — "Calm Cinematic Piano Soft"
 * by PaulYudin, Pixabay Content License) via Web Audio API for sample-
 * accurate gapless looping and smooth, route-aware volume ducking.
 *
 * Why Web Audio (not a plain <audio loop>)?
 *   • <audio loop> restarts with a perceptible click (most browsers).
 *   • AudioBufferSourceNode.loop = true is gapless by construction.
 *   • We also need per-route linearRampToValueAtTime fades, which <audio>
 *     cannot do natively.
 *
 * No synthesis. Every tone the user hears comes from the MP3 file.
 * A parallel "air" branch (tiny delay + lowpass) adds a subtle breathy
 * halo so the loop feels spacious — but it's only the *same* signal,
 * not a separate musical part.
 *
 * Route-aware volume (Laura's "smart ducking"):
 *   • /legal/*, /settings/*, /onboarding/consent, /admin/*  → paused
 *   • /chat/*                                               → 0.08 (under voice)
 *   • /login, /signup                                        → 0.18
 *   • default                                                → 0.22 (soft, barely there)
 *
 * Fade-in on first start: 4s linear ramp.
 * Route change: 1.6s linear ramp.
 *
 * Autoplay policy:
 *   Browsers forbid AudioContext.resume() before a user gesture.
 *   We build the graph eagerly, keep it suspended, and resume on the
 *   first click/tap/scroll/keypress. User OFF preference is persisted.
 *
 * Preferences:
 *   • echoes_ambient_pref: "on" | "off"
 */
import { useEffect, useRef, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";

const AUDIO_SRC = "/media/background.mp3";
const PREF_KEY = "echoes_ambient_pref";

function readPref() {
    try {
        const v = localStorage.getItem(PREF_KEY);
        if (v === "on" || v === "off") return v;
    } catch { /* noop */ }
    return null;
}

// Route → target master-gain mapping.
// Defaults are low on purpose — Laura's brief: "volume very low and soft,
// must feel safe, not disturbing".
function targetGainFor(pathname) {
    const p = pathname || "/";
    if (
        p.startsWith("/legal") ||
        p.startsWith("/settings") ||
        p.startsWith("/onboarding/consent") ||
        p.startsWith("/admin")
    ) {
        return 0.0;   // paused — sensitive/concentration zones
    }
    if (p.startsWith("/chat/")) return 0.07;       // even softer under ElevenLabs voice
    if (p === "/login" || p === "/signup") return 0.15;
    return 0.19;      // default ambience — soft, warm, immersive
}

// Subtle per-presence pitch shift via playbackRate. Small enough to feel
// like a harmonic colour change, not a pitch bend.
//   parent   → 0.955  (≈ -0.8 semitone: rounder, nurturing)
//   partner  → 0.975  (≈ -0.4 semitone: warm, intimate)
//   friend   → 1.000  (reference)
function presenceRateFor(relationship) {
    const r = (relationship || "").toLowerCase();
    if (r.includes("parent") || r.includes("madre") || r.includes("padre") ||
        r.includes("mother") || r.includes("father") || r.includes("mamma") ||
        r.includes("papa") || r.includes("papà")) return 0.955;
    if (r.includes("partner") || r.includes("compagn") || r.includes("spouse") ||
        r.includes("husband") || r.includes("wife") || r.includes("mari") ||
        r.includes("coniuge")) return 0.975;
    return 1.0;
}

export default function GlobalAmbientAudio() {
    const location = useLocation();
    const ctxRef = useRef(null);
    const bufferRef = useRef(null);
    const sourceRef = useRef(null);
    const masterRef = useRef(null);
    const airGainRef = useRef(null);
    const decodedRef = useRef(false);
    const [enabled, setEnabled] = useState(false);
    const [running, setRunning] = useState(false);

    // Build the audio graph + decode the MP3 (once per session).
    // Graph:
    //   source ─┬─► master (dry) ─► destination
    //           └─► delay(80ms) ─► lowpass(2.2kHz) ─► airGain (wet halo) ─► destination
    const ensureGraph = useCallback(async () => {
        if (ctxRef.current && decodedRef.current) return ctxRef.current;
        const AC = window.AudioContext || window.webkitAudioContext;
        if (!AC) return null;

        let ctx = ctxRef.current;
        if (!ctx) {
            ctx = new AC();
            ctxRef.current = ctx;

            const master = ctx.createGain();
            master.gain.value = 0.0;
            master.connect(ctx.destination);
            masterRef.current = master;

            // "Airy" halo — a gentle parallel branch. Same material as the
            // dry MP3, delayed ~80ms and low-passed, at very low level.
            // Adds space without introducing any new melodic content.
            const airGain = ctx.createGain();
            airGain.gain.value = 0.0;
            airGain.connect(ctx.destination);
            airGainRef.current = airGain;
        }

        if (!bufferRef.current) {
            try {
                const res = await fetch(AUDIO_SRC, { cache: "force-cache" });
                if (!res.ok) throw new Error(`fetch ${res.status}`);
                const arr = await res.arrayBuffer();
                // decodeAudioData: prefer promise form; fall back for Safari.
                const buf = await new Promise((resolve, reject) => {
                    try {
                        const p = ctx.decodeAudioData(arr, resolve, reject);
                        if (p && typeof p.then === "function") p.then(resolve, reject);
                    } catch (e) { reject(e); }
                });
                bufferRef.current = buf;
                decodedRef.current = true;
            } catch (e) {
                console.warn("[GlobalAmbientAudio] decode failed:", e);
                return null;
            }
        }
        return ctx;
    }, []);

    // Start (or re-start) the looping source node and wire it.
    const startSource = useCallback(() => {
        const ctx = ctxRef.current;
        const buf = bufferRef.current;
        const master = masterRef.current;
        const airGain = airGainRef.current;
        if (!ctx || !buf || !master || !airGain) return;

        // Tear down any previous source (route returns after pause, etc.)
        if (sourceRef.current) {
            try { sourceRef.current.stop(); } catch { /* noop */ }
            try { sourceRef.current.disconnect(); } catch { /* noop */ }
            sourceRef.current = null;
        }

        const src = ctx.createBufferSource();
        src.buffer = buf;
        src.loop = true;
        // Small loop trim: start at 0.05s and end 0.05s before the real
        // end to hide any engineered silence/click at the file boundaries.
        src.loopStart = 0.05;
        src.loopEnd = Math.max(0.1, buf.duration - 0.05);

        // Dry path
        src.connect(master);

        // Wet "air" path
        const delay = ctx.createDelay(0.3);
        delay.delayTime.value = 0.08;
        const lp = ctx.createBiquadFilter();
        lp.type = "lowpass";
        lp.frequency.value = 2200;
        lp.Q.value = 0.4;
        src.connect(delay);
        delay.connect(lp);
        lp.connect(airGain);

        src.start(0);
        sourceRef.current = src;
    }, []);

    // Ramp master gain to the route's target. Also brings the air branch
    // to 18% of the master target — always subordinate, never audible on
    // its own.
    const rampToTarget = useCallback((rampSeconds) => {
        const ctx = ctxRef.current;
        const master = masterRef.current;
        const airGain = airGainRef.current;
        if (!ctx || !master || !airGain) return;
        const now = ctx.currentTime;
        const t = targetGainFor(location.pathname);
        master.gain.cancelScheduledValues(now);
        master.gain.setValueAtTime(master.gain.value, now);
        master.gain.linearRampToValueAtTime(t, now + rampSeconds);
        airGain.gain.cancelScheduledValues(now);
        airGain.gain.setValueAtTime(airGain.gain.value, now);
        airGain.gain.linearRampToValueAtTime(t * 0.18, now + rampSeconds);
    }, [location.pathname]);

    const resumeAndRamp = useCallback(async () => {
        const ctx = await ensureGraph();
        if (!ctx) return;
        try {
            if (ctx.state !== "running") await ctx.resume();
        } catch {
            return; // autoplay blocked; wait for another gesture
        }
        if (!sourceRef.current) startSource();
        // First-start: deep, emotional 5.5s fade-in (Laura's refinement).
        // Route changes keep a smoother 2.0s ramp for continuity.
        rampToTarget(running ? 2.0 : 5.5);
        setRunning(true);
        if (typeof window !== "undefined") {
            window.dispatchEvent(new CustomEvent("ambientaudio:started"));
        }
    }, [ensureGraph, startSource, rampToTarget, running]);

    const stop = useCallback(() => {
        const ctx = ctxRef.current;
        const master = masterRef.current;
        const airGain = airGainRef.current;
        if (!ctx || !master || !airGain) return;
        const now = ctx.currentTime;
        master.gain.cancelScheduledValues(now);
        master.gain.setValueAtTime(master.gain.value, now);
        master.gain.linearRampToValueAtTime(0, now + 0.6);
        airGain.gain.cancelScheduledValues(now);
        airGain.gain.setValueAtTime(airGain.gain.value, now);
        airGain.gain.linearRampToValueAtTime(0, now + 0.6);
        setTimeout(() => {
            try { ctx.suspend(); } catch { /* noop */ }
            if (sourceRef.current) {
                try { sourceRef.current.stop(); } catch { /* noop */ }
                try { sourceRef.current.disconnect(); } catch { /* noop */ }
                sourceRef.current = null;
            }
        }, 800);
        setRunning(false);
        if (typeof window !== "undefined") {
            window.dispatchEvent(new CustomEvent("ambientaudio:stopped"));
        }
    }, []);

    // ── Bootstrap on mount: honor pref, try to resume, listen for gesture.
    useEffect(() => {
        const pref = readPref();
        if (pref === "off") {
            setEnabled(false);
            return;
        }
        setEnabled(true);
        // Fire & forget — ensureGraph decodes, then resume attempts autoplay.
        const tryNow = setTimeout(() => { resumeAndRamp(); }, 120);
        const onGesture = () => { resumeAndRamp(); };
        const EVENTS = ["pointerdown", "touchstart", "click", "keydown", "scroll", "wheel"];
        const opts = { capture: true, passive: true };
        EVENTS.forEach((ev) => document.addEventListener(ev, onGesture, opts));
        const poll = setInterval(() => {
            const ctx = ctxRef.current;
            if (ctx && ctx.state === "running") {
                EVENTS.forEach((ev) => document.removeEventListener(ev, onGesture, opts));
                clearInterval(poll);
            }
        }, 300);
        return () => {
            clearTimeout(tryNow);
            EVENTS.forEach((ev) => document.removeEventListener(ev, onGesture, opts));
            clearInterval(poll);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ── Route change: ramp master to the new route's target gain.
    useEffect(() => {
        const ctx = ctxRef.current;
        const master = masterRef.current;
        if (!ctx || !master || !enabled) return;
        rampToTarget(2.0);
        // If we were silent on a sensitive page and the user navigates
        // back to a "speaking" route, make sure context is running and
        // a source is playing.
        const t = targetGainFor(location.pathname);
        if (t > 0) {
            if (ctx.state !== "running") ctx.resume().catch(() => {});
            if (!sourceRef.current) startSource();
            setRunning(true);
        }
    }, [location.pathname, enabled, rampToTarget, startSource]);

    // ── External controls (AmbientAudioToggle + Conversation.jsx).
    useEffect(() => {
        const onToggle = () => {
            if (enabled) {
                setEnabled(false);
                try { localStorage.setItem(PREF_KEY, "off"); } catch { /* noop */ }
                stop();
            } else {
                setEnabled(true);
                try { localStorage.setItem(PREF_KEY, "on"); } catch { /* noop */ }
                resumeAndRamp();
            }
        };
        const onQuery = () => {
            window.dispatchEvent(new CustomEvent("ambientaudio:status", {
                detail: { enabled, running },
            }));
        };
        // Per-presence tonal colour via playbackRate (smooth 1.5s ramp).
        const onSetPresence = (e) => {
            const src = sourceRef.current;
            const ctx = ctxRef.current;
            if (!src || !ctx) return;
            const rate = presenceRateFor(e?.detail?.relationship);
            if (Math.abs(src.playbackRate.value - rate) < 0.002) return;
            const now = ctx.currentTime;
            try {
                src.playbackRate.cancelScheduledValues(now);
                src.playbackRate.setValueAtTime(src.playbackRate.value, now);
                src.playbackRate.linearRampToValueAtTime(rate, now + 1.5);
            } catch { /* noop */ }
        };
        const onClearPresence = () => {
            const src = sourceRef.current;
            const ctx = ctxRef.current;
            if (!src || !ctx) return;
            const now = ctx.currentTime;
            try {
                src.playbackRate.cancelScheduledValues(now);
                src.playbackRate.setValueAtTime(src.playbackRate.value, now);
                src.playbackRate.linearRampToValueAtTime(1.0, now + 1.5);
            } catch { /* noop */ }
        };
        window.addEventListener("ambientaudio:toggle", onToggle);
        window.addEventListener("ambientaudio:query", onQuery);
        window.addEventListener("ambientaudio:setpresence", onSetPresence);
        window.addEventListener("ambientaudio:clearpresence", onClearPresence);
        setTimeout(onQuery, 100);
        return () => {
            window.removeEventListener("ambientaudio:toggle", onToggle);
            window.removeEventListener("ambientaudio:query", onQuery);
            window.removeEventListener("ambientaudio:setpresence", onSetPresence);
            window.removeEventListener("ambientaudio:clearpresence", onClearPresence);
        };
    }, [enabled, running, stop, resumeAndRamp]);

    // ── Cleanup on unmount (only on full SPA teardown).
    useEffect(() => {
        return () => {
            if (sourceRef.current) {
                try { sourceRef.current.stop(); } catch { /* noop */ }
                try { sourceRef.current.disconnect(); } catch { /* noop */ }
                sourceRef.current = null;
            }
            const ctx = ctxRef.current;
            if (ctx) { try { ctx.close(); } catch { /* noop */ } }
            ctxRef.current = null;
            masterRef.current = null;
            airGainRef.current = null;
        };
    }, []);

    return null; // headless
}
